// practiceOffline.ts · v12.10.30 (#2255 practice offline - the signal, Check status, Report issue with its offline queue) · 2026-11-03 · VoiceSpectrum AI
//
// v12.10.30 (#2255, Tushar 11/3): while Support Ops has the practice offline "every screen of the practice app and its sign-in page show the
// offline page". Two ways in: the page asks GET /api/public/practice-status on load (sign-in page, no sign-in needed), and any practice
// call answers 423 practice_offline (api.ts calls reportPracticeOffline). Report issue (all three offline pages) posts to
// /api/public/support-report; "you're offline" reports wait in localStorage until the connection is back, "we'll be right back"
// reports are retried until VoiceSpectrum answers again. These calls are anonymous on purpose (plain fetch, no token): the page must
// work whatever the session's state.
import { buildUrl } from './api';

export type CallOption = 'voicemail' | 'keep' | 'none';
export interface PracticeOfflineInfo {
  practice?: string | null;
  callOption?: CallOption | null;
  callLabel?: string | null;
  since?: string | null;
  autoTextsStopped?: boolean;
}
export interface PracticeStatus extends PracticeOfflineInfo { offline: boolean; slug?: string | null; checkedAt?: string }

type Listener = (info: PracticeOfflineInfo) => void;
/** api.ts fires this window event (no import - api.ts stays free of this file) when a practice call answers 423 practice_offline. */
export const PRACTICE_OFFLINE_EVENT = 'vs:practice-offline';

export function reportPracticeOffline(body: unknown): void {
  try { window.dispatchEvent(new CustomEvent(PRACTICE_OFFLINE_EVENT, { detail: body })); } catch { /* no window */ }
}
export function onPracticeOffline(l: Listener): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent).detail;
    try { l((d && typeof d === 'object' ? d : {}) as PracticeOfflineInfo); } catch { /* a listener must not break the caller */ }
  };
  window.addEventListener(PRACTICE_OFFLINE_EVENT, h);
  return () => window.removeEventListener(PRACTICE_OFFLINE_EVENT, h);
}

// ── the practice, the person, this browser ──────────────────────────────────────────────────────────────────────────
/** Same rule as the sign-in page (pages/login/parts.tsx resolveTenantSlug) - copied, so this file never imports the login pages. */
export function practiceSlug(): string | null {
  try { const s = sessionStorage.getItem('vs.tenantSlug'); if (s) return s; } catch { /* storage blocked */ }
  const q = new URLSearchParams(window.location.search).get('tenant')?.trim();
  if (q) return q;
  const host = window.location.host.toLowerCase();
  if (host.endsWith('.voicespectrum.ai')) {
    const parts = host.split('.');
    if (parts.length >= 3 && parts[0] !== 'app' && parts[0] !== 'api') return parts[0];
  }
  const c = tokenClaims();   // a signed-in person on the platform address: the practice their token names
  return c && typeof c.vs_tenant_slug === 'string' && c.vs_tenant_slug ? c.vs_tenant_slug : null;
}

/** The signed-in person's token claims (display only - never trusted for anything). */
function tokenClaims(): Record<string, unknown> | null {
  try {
    const t = localStorage.getItem('vs_t2_token');
    if (!t) return null;
    const p = t.split('.')[1];
    if (!p) return null;
    const json = decodeURIComponent(atob(p.replace(/-/g, '+').replace(/_/g, '/')).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(json) as Record<string, unknown>;
  } catch { return null; }
}

/** "Jane Doe" / jane@clinic.com when someone is signed in on this browser. */
export function currentUserLabel(): string | null {
  const c = tokenClaims();
  if (!c) return null;
  const name = [c.given_name, c.family_name].filter((x) => typeof x === 'string' && x).join(' ') || (typeof c.name === 'string' ? c.name : '');
  const email = typeof c.email === 'string' ? c.email : typeof c.sub === 'string' ? c.sub : '';
  return (name || email || null) as string | null;
}
export function currentUserContact(): string {
  const c = tokenClaims();
  const v = c && (typeof c.email === 'string' ? c.email : typeof c.sub === 'string' ? c.sub : '');
  return v && !String(v).startsWith('user:') ? String(v) : '';
}

/** v12.10.30 (#2255): "VoiceSpectrum Support visits still work" - a support visit never sees the offline page. */
export function isSupportVisit(): boolean {
  try {
    if (window.location.pathname.startsWith('/support-entry')) return true;
    if (sessionStorage.getItem('vs.supportVisit')) return true;
  } catch { /* storage blocked */ }
  const c = tokenClaims();
  return !!(c && typeof c.vs_imp_by === 'string' && c.vs_imp_by);
}

function clientId(): string {
  try {
    let id = localStorage.getItem('vs.clientId');
    if (!id) { id = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('vs.clientId', id); }
    return id;
  } catch { return ''; }
}

/** "Chrome 130 on Windows" - enough for Support to recognise the device. */
export function deviceLabel(): string {
  const ua = navigator.userAgent || '';
  const m = (re: RegExp) => ua.match(re)?.[1];
  const browser = m(/Edg\/(\d+)/) ? `Edge ${m(/Edg\/(\d+)/)}` : m(/OPR\/(\d+)/) ? `Opera ${m(/OPR\/(\d+)/)}`
    : m(/Chrome\/(\d+)/) ? `Chrome ${m(/Chrome\/(\d+)/)}` : m(/Firefox\/(\d+)/) ? `Firefox ${m(/Firefox\/(\d+)/)}`
    : m(/Version\/(\d+).*Safari/) ? `Safari ${m(/Version\/(\d+).*Safari/)}` : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : 'unknown';
  return `${browser} on ${os}`;
}

export function timeText(d: Date | string | null | undefined): string {
  const x = d ? new Date(d) : null;
  return x && !isNaN(x.getTime()) ? x.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
}

// ── anonymous calls ─────────────────────────────────────────────────────────────────────────────────────────────────
async function publicCall<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<{ status: number; data: T | null }> {
  const res = await fetch(buildUrl(path), {
    method, cache: 'no-store',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data: T | null = null;
  try { data = await res.json() as T; } catch { data = null; }
  return { status: res.status, data };
}

/** Check status: is the practice offline right now? Throws when VoiceSpectrum can't be reached. */
export async function fetchPracticeStatus(slug: string | null = practiceSlug()): Promise<PracticeStatus> {
  if (!slug) throw new Error('no practice');   // can't tell - never read as "back online"
  const r = await publicCall<PracticeStatus>('GET', `/api/public/practice-status?slug=${encodeURIComponent(slug)}&t=${Date.now()}`);
  if (r.status !== 200 || !r.data) throw new Error(`status ${r.status}`);
  return r.data;
}

/** The sign-in page's Contact support form, from the offline page (same API: /api/public/support-request). */
export async function postPublic<T>(path: string, body: unknown): Promise<{ status: number; data: T | null }> {
  return publicCall<T>('POST', path, body);
}

// ── Report issue ────────────────────────────────────────────────────────────────────────────────────────────────────
export type ReportKind = 'taken_offline' | 'offline' | 'maintenance';
export interface ReportAnswer { status: 'created' | 'added' | 'already' | 'online' | 'queued' | 'limited' | 'error'; reference?: string; at?: string; count?: number }
interface PendingReport { kind: ReportKind; slug: string | null; page: string; note: string | null; user: string | null; device: string; clientId: string; clickedAt: string; downSince: string | null }

const QUEUE_KEY = 'vs.pendingReports';
function readQueue(): PendingReport[] {
  try { const v = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); return Array.isArray(v) ? v as PendingReport[] : []; } catch { return []; }
}
function writeQueue(q: PendingReport[]): void {
  try { if (q.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-20))); else localStorage.removeItem(QUEUE_KEY); } catch { /* storage blocked */ }
}

function build(kind: ReportKind, note: string, downSince: number | null): PendingReport {
  return {
    kind, slug: practiceSlug(), page: window.location.pathname, note: note.trim() || null, user: currentUserLabel(), device: deviceLabel(),
    clientId: clientId(), clickedAt: new Date().toISOString(), downSince: downSince ? new Date(downSince).toISOString() : null,
  };
}

async function post(r: PendingReport): Promise<ReportAnswer> {
  const res = await publicCall<ReportAnswer>('POST', '/api/public/support-report', r);
  if (res.status === 200 && res.data) return res.data;
  if (res.status === 429) return { status: 'limited' };
  if (res.status >= 500) throw new Error(`status ${res.status}`);   // VoiceSpectrum is not answering yet - keep it
  return { status: 'error' };
}

let flushTimer: number | undefined;
/** Send what waited (you're offline / we'll be right back). Safe to call any time; keeps retrying every 15 s while something waits. */
export async function flushReports(): Promise<void> {
  const q = readQueue();
  if (!q.length) { if (flushTimer) { window.clearInterval(flushTimer); flushTimer = undefined; } return; }
  const left: PendingReport[] = [];
  for (const r of q) {
    try { await post(r); } catch { left.push(r); }
  }
  writeQueue(left);
  if (left.length && !flushTimer) flushTimer = window.setInterval(() => { void flushReports(); }, 15_000);
  if (!left.length && flushTimer) { window.clearInterval(flushTimer); flushTimer = undefined; }
}
if (typeof window !== 'undefined') window.addEventListener('online', () => { void flushReports(); });

/**
 * v12.10.30 (#2255): Report issue. "offline" (the person's own internet) is queued here and sent when the connection returns;
 * "maintenance" is sent now and, if VoiceSpectrum can't be reached, queued and retried ("We'll send this as soon as we can reach
 * VoiceSpectrum"); "taken_offline" is sent now.
 */
export async function sendReport(kind: ReportKind, note: string, downSince: number | null = null): Promise<ReportAnswer> {
  const r = build(kind, note, downSince);
  if (kind === 'offline' && typeof navigator !== 'undefined' && navigator.onLine === false) {
    writeQueue([...readQueue(), r]); void flushReports();
    return { status: 'queued' };
  }
  try { return await post(r); }
  catch {
    if (kind === 'taken_offline') return { status: 'error' };
    writeQueue([...readQueue(), r]); void flushReports();
    return { status: 'queued' };
  }
}

/** One sentence for the answer, under the button. */
export function reportAnswerText(kind: ReportKind, a: ReportAnswer): string {
  switch (a.status) {
    case 'created': case 'added': return `Reported - VoiceSpectrum Support has it${a.reference ? ` (${a.reference})` : ''}.`;
    case 'already': return `Already reported at ${timeText(a.at)}${a.reference ? ` (${a.reference})` : ''}.`;
    case 'queued': return kind === 'offline' ? 'Saved - we’ll send it as soon as your connection is back.' : 'We’ll send this as soon as we can reach VoiceSpectrum.';
    case 'online': return 'Your account is back online.';
    case 'limited': return 'We already have several reports from this network - VoiceSpectrum Support is on it.';
    default: return 'We couldn’t send that just now. Try again in a moment.';
  }
}
